import React, { useState, useMemo, useRef } from 'react';
import {
  Truck, Package, CheckCircle, MapPin, Phone, DollarSign,
  User, Clock, RefreshCw, Search, Filter, ChevronRight,
  AlertCircle, Pen, X, Loader2, Calendar, MoreVertical
} from 'lucide-react';

// Mock Data
const mockDeliveryPackages = [
  // Ready for Delivery
  { id: 'PKG-001', tracking: 'FS-2025-8421', customer: 'Marcus Johnson', customerId: 'C001', phone: '+1 876 555-0101', parish: 'Kingston', weight: 4.5, invoiceStatus: 'approved', invoiceTotal: 2450, status: 'ready_for_delivery' },
  { id: 'PKG-002', tracking: 'FS-2025-8422', customer: 'Marcus Johnson', customerId: 'C001', phone: '+1 876 555-0101', parish: 'Kingston', weight: 2.1, invoiceStatus: 'approved', invoiceTotal: 1450, status: 'ready_for_delivery' },
  { id: 'PKG-003', tracking: 'FS-2025-8423', customer: 'Emily Davis', customerId: 'C002', phone: '+1 876 555-0102', parish: 'St. Andrew', weight: 6.8, invoiceStatus: 'paid', invoiceTotal: 3450, status: 'ready_for_delivery' },
  { id: 'PKG-004', tracking: 'FS-2025-8424', customer: 'James Brown', customerId: 'C003', phone: '+1 876 555-0103', parish: 'Portmore', weight: 1.2, invoiceStatus: 'approved', invoiceTotal: 1200, status: 'ready_for_delivery' },
  
  // Out for Delivery
  { id: 'PKG-005', tracking: 'FS-2025-8425', customer: 'Sarah Williams', customerId: 'C004', phone: '+1 876 555-0104', parish: 'Kingston', weight: 8.5, invoiceStatus: 'paid', invoiceTotal: 4450, status: 'out_for_delivery' },
  { id: 'PKG-006', tracking: 'FS-2025-8426', customer: 'Sarah Williams', customerId: 'C004', phone: '+1 876 555-0104', parish: 'Kingston', weight: 3.2, invoiceStatus: 'paid', invoiceTotal: 1950, status: 'out_for_delivery' },
  { id: 'PKG-007', tracking: 'FS-2025-8427', customer: 'Michael Chen', customerId: 'C005', phone: '+1 876 555-0105', parish: 'Spanish Town', weight: 5.0, invoiceStatus: 'approved', invoiceTotal: 2950, status: 'out_for_delivery' },
  
  // Delivered Today
  { id: 'PKG-008', tracking: 'FS-2025-8428', customer: 'Lisa Anderson', customerId: 'C006', phone: '+1 876 555-0106', parish: 'Montego Bay', weight: 2.5, invoiceStatus: 'paid', invoiceTotal: 1700, status: 'delivered', deliveredAt: '10:30 AM' },
  { id: 'PKG-009', tracking: 'FS-2025-8429', customer: 'Robert Taylor', customerId: 'C007', phone: '+1 876 555-0107', parish: 'Kingston', weight: 7.2, invoiceStatus: 'paid', invoiceTotal: 3950, status: 'delivered', deliveredAt: '11:45 AM' },
];

const COLUMNS = [
  { id: 'ready_for_delivery', label: 'Ready for Delivery', icon: Package, color: 'column-ready' },
  { id: 'out_for_delivery', label: 'Out for Delivery', icon: Truck, color: 'column-out' },
  { id: 'delivered', label: 'Delivered Today', icon: CheckCircle, color: 'column-delivered' },
];

const formatJMD = (amount) => `J$${(amount || 0).toLocaleString()}`;

// Customer Delivery Card (grouped by customer)
const CustomerDeliveryCard = ({ customer, packages, onStartDelivery, onCompleteDelivery }) => {
  const [expanded, setExpanded] = useState(false);
  
  const totalPackages = packages.length;
  const totalWeight = packages.reduce((sum, p) => sum + (p.weight || 0), 0);
  const totalAmount = packages.reduce((sum, p) => sum + (p.invoiceTotal || 0), 0);
  const allPaid = packages.every(p => p.invoiceStatus === 'paid');
  const isOutForDelivery = packages.some(p => p.status === 'out_for_delivery');
  const isDelivered = packages.every(p => p.status === 'delivered');

  return (
    <div className="delivery-card" data-testid={`delivery-card-${customer.id}`}>
      <div className="delivery-card-content">
        {/* Header */}
        <div className="delivery-header">
          <div className="customer-info">
            <span className="customer-name">{customer.name}</span>
            {totalPackages > 1 && (
              <span className="pkg-count">{totalPackages} pkgs</span>
            )}
          </div>
          <span className={`payment-badge ${allPaid ? 'paid' : 'cod'}`}>
            {allPaid ? 'Prepaid' : `$${totalAmount.toLocaleString()} COD`}
          </span>
        </div>

        {/* Location */}
        <div className="delivery-location">
          <MapPin size={14} />
          <span>{customer.parish}</span>
        </div>

        {/* Contact */}
        {customer.phone && (
          <a href={`tel:${customer.phone}`} className="delivery-phone">
            <Phone size={14} />
            {customer.phone}
          </a>
        )}

        {/* Summary */}
        <div className="delivery-summary">
          <span><Package size={14} /> {totalWeight.toFixed(1)} lbs</span>
          <span><DollarSign size={14} /> {formatJMD(totalAmount)}</span>
        </div>

        {/* Expand packages */}
        {totalPackages > 1 && (
          <button className="expand-btn" onClick={() => setExpanded(!expanded)}>
            <ChevronRight size={16} className={expanded ? 'rotated' : ''} />
            {expanded ? 'Hide' : 'Show'} {totalPackages} packages
          </button>
        )}

        {/* Actions */}
        {!isDelivered && (
          <div className="delivery-actions">
            {!isOutForDelivery && (
              <button className="btn-start" onClick={() => onStartDelivery(packages)}>
                <Truck size={16} />
                Start Delivery ({totalPackages})
              </button>
            )}
            {isOutForDelivery && (
              <button className="btn-complete" onClick={() => onCompleteDelivery(packages, customer)}>
                <CheckCircle size={16} />
                Complete Delivery ({totalPackages})
              </button>
            )}
          </div>
        )}

        {isDelivered && (
          <div className="delivered-badge">
            <CheckCircle size={16} />
            Delivered
          </div>
        )}
      </div>

      {/* Expanded Package List */}
      {expanded && (
        <div className="expanded-packages">
          {packages.map(pkg => (
            <div key={pkg.id} className="package-item">
              <div className="package-info">
                <span className="tracking">{pkg.tracking}</span>
                <span className="weight">{pkg.weight} lbs</span>
              </div>
              <div className="package-amount">
                <span className="amount">{formatJMD(pkg.invoiceTotal)}</span>
                <span className={`status ${pkg.invoiceStatus}`}>
                  {pkg.invoiceStatus === 'paid' ? 'Paid' : 'COD'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Delivery Column Component
const DeliveryColumn = ({ column, groupedPackages, onStartDelivery, onCompleteDelivery }) => {
  const Icon = column.icon;
  const customerCount = Object.keys(groupedPackages).length;
  const packageCount = Object.values(groupedPackages).reduce((sum, g) => sum + g.packages.length, 0);

  return (
    <div className={`delivery-column ${column.color}`}>
      <div className="column-header">
        <div className="column-title">
          <div className="column-icon">
            <Icon size={18} />
          </div>
          <span>{column.label}</span>
        </div>
        <span className="column-count">{customerCount}</span>
      </div>

      <div className="column-content">
        {customerCount === 0 ? (
          <div className="empty-column">
            <Package size={32} />
            <p>No deliveries</p>
          </div>
        ) : (
          Object.entries(groupedPackages).map(([customerId, data]) => (
            <CustomerDeliveryCard
              key={customerId}
              customer={data.customer}
              packages={data.packages}
              onStartDelivery={onStartDelivery}
              onCompleteDelivery={onCompleteDelivery}
            />
          ))
        )}
      </div>

      <div className="column-footer">
        {packageCount} package{packageCount !== 1 ? 's' : ''}
      </div>
    </div>
  );
};

// Signature Pad Component
const SignaturePad = ({ onSignatureChange }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasContent, setHasContent] = useState(false);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
    
    if (!hasContent) {
      setHasContent(true);
      onSignatureChange(true);
    }
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasContent(false);
    onSignatureChange(false);
  };

  return (
    <div className="signature-pad">
      <canvas
        ref={canvasRef}
        width={360}
        height={120}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
      />
      <button className="clear-signature" onClick={clearSignature} type="button">
        <X size={14} /> Clear
      </button>
    </div>
  );
};

export default function TenantDeliveryBoard() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedParish, setSelectedParish] = useState('all');
  const [viewMode, setViewMode] = useState('grouped');
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [selectedPackages, setSelectedPackages] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [hasSignature, setHasSignature] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [deliveryNotes, setDeliveryNotes] = useState('');
  
  // Delivery Scanner State
  const [scanMode, setScanMode] = useState(false);
  const [scanInput, setScanInput] = useState('');
  const [recentScans, setRecentScans] = useState([]);

  // Group packages by status
  const groupedByStatus = useMemo(() => {
    const filtered = mockDeliveryPackages.filter(pkg => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!pkg.tracking.toLowerCase().includes(query) &&
            !pkg.customer.toLowerCase().includes(query) &&
            !pkg.phone?.includes(query)) {
          return false;
        }
      }
      if (selectedParish !== 'all' && pkg.parish !== selectedParish) {
        return false;
      }
      return true;
    });

    const groups = {
      ready_for_delivery: {},
      out_for_delivery: {},
      delivered: {},
    };

    filtered.forEach(pkg => {
      const status = pkg.status;
      const customerId = pkg.customerId;
      
      if (!groups[status]) return;
      
      if (!groups[status][customerId]) {
        groups[status][customerId] = {
          customer: {
            id: customerId,
            name: pkg.customer,
            phone: pkg.phone,
            parish: pkg.parish,
          },
          packages: [],
        };
      }
      groups[status][customerId].packages.push(pkg);
    });

    return groups;
  }, [searchQuery, selectedParish]);

  // Get unique parishes
  const parishes = useMemo(() => {
    return [...new Set(mockDeliveryPackages.map(p => p.parish))].sort();
  }, []);

  // Stats
  const stats = useMemo(() => ({
    ready: Object.values(groupedByStatus.ready_for_delivery).reduce((sum, g) => sum + g.packages.length, 0),
    readyCustomers: Object.keys(groupedByStatus.ready_for_delivery).length,
    out: Object.values(groupedByStatus.out_for_delivery).reduce((sum, g) => sum + g.packages.length, 0),
    outCustomers: Object.keys(groupedByStatus.out_for_delivery).length,
    delivered: Object.values(groupedByStatus.delivered).reduce((sum, g) => sum + g.packages.length, 0),
    deliveredCustomers: Object.keys(groupedByStatus.delivered).length,
    total: mockDeliveryPackages.length,
  }), [groupedByStatus]);

  const handleStartDelivery = (packages) => {
    // Mock: update status to out_for_delivery
    console.log('Starting delivery for:', packages.map(p => p.tracking));
    alert(`Started delivery for ${packages.length} package(s)`);
  };

  const handleCompleteDelivery = (packages, customer) => {
    setSelectedPackages(packages);
    setSelectedCustomer(customer);
    setHasSignature(false);
    setDeliveryNotes('');
    setShowCompleteDialog(true);
  };

  const handleConfirmDelivery = () => {
    if (!hasSignature) {
      alert('Please capture customer signature');
      return;
    }
    console.log('Completing delivery:', selectedPackages.map(p => p.tracking));
    alert(`Delivered ${selectedPackages.length} package(s) to ${selectedCustomer.name}`);
    setShowCompleteDialog(false);
    setSelectedPackages([]);
    setSelectedCustomer(null);
  };

  const handleDeliveryScan = (e) => {
    e.preventDefault();
    if (!scanInput.trim() || scanInput.length < 4) return;
    
    // Mock scan result
    const found = mockDeliveryPackages.find(p => 
      p.tracking.toLowerCase().includes(scanInput.toLowerCase())
    );
    
    const scanResult = {
      tracking: scanInput,
      customer: found?.customer || 'Not Found',
      status: found ? 'Delivered' : 'Error',
      success: !!found,
      time: new Date().toLocaleTimeString(),
    };
    
    setRecentScans(prev => [scanResult, ...prev.slice(0, 9)]);
    setScanInput('');
  };

  // Calculate COD total for selected packages
  const codTotal = selectedPackages
    .filter(p => p.invoiceStatus !== 'paid')
    .reduce((sum, p) => sum + (p.invoiceTotal || 0), 0);

  return (
    <div className="delivery-board-page" data-testid="delivery-board">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <div className="header-icon">
            <Truck size={28} />
          </div>
          <div className="header-text">
            <h1>Delivery Board</h1>
            <p>{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
          </div>
        </div>
        <div className="header-actions">
          <div className="view-toggle">
            <button 
              className={viewMode === 'grouped' ? 'active' : ''}
              onClick={() => setViewMode('grouped')}
            >
              By Customer
            </button>
            <button 
              className={viewMode === 'individual' ? 'active' : ''}
              onClick={() => setViewMode('individual')}
            >
              Individual
            </button>
          </div>
          <button className="btn-refresh">
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="delivery-stats">
        <div className="stat-card">
          <span className="stat-label">Ready</span>
          <span className="stat-value">{viewMode === 'grouped' ? stats.readyCustomers : stats.ready}</span>
          <span className="stat-sub">{viewMode === 'grouped' ? `${stats.ready} pkgs` : 'packages'}</span>
        </div>
        <div className="stat-card stat-orange">
          <span className="stat-label">Out for Delivery</span>
          <span className="stat-value">{viewMode === 'grouped' ? stats.outCustomers : stats.out}</span>
          <span className="stat-sub">{viewMode === 'grouped' ? `${stats.out} pkgs` : 'packages'}</span>
        </div>
        <div className="stat-card stat-green">
          <span className="stat-label">Delivered Today</span>
          <span className="stat-value">{viewMode === 'grouped' ? stats.deliveredCustomers : stats.delivered}</span>
          <span className="stat-sub">{viewMode === 'grouped' ? `${stats.delivered} pkgs` : 'packages'}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Total Packages</span>
          <span className="stat-value">{stats.total}</span>
        </div>
      </div>

      {/* Delivery Scanner */}
      <div className={`delivery-scanner ${scanMode ? 'active' : ''}`}>
        <div className="scanner-header">
          <div className="scanner-title">
            <div className="scanner-icon">
              <Package size={20} />
            </div>
            <div>
              <h3>Delivery Scanner</h3>
              <p>Scan or enter tracking to mark as delivered</p>
            </div>
          </div>
          <button 
            className={`scanner-toggle ${scanMode ? 'active' : ''}`}
            onClick={() => setScanMode(!scanMode)}
          >
            {scanMode ? 'Close Scanner' : 'Open Scanner'}
          </button>
        </div>

        {scanMode && (
          <div className="scanner-content">
            <form className="scan-form" onSubmit={handleDeliveryScan}>
              <div className="scan-input-wrapper">
                <Package size={18} />
                <input
                  type="text"
                  placeholder="Enter last 4 digits or full tracking #..."
                  value={scanInput}
                  onChange={(e) => setScanInput(e.target.value)}
                  autoFocus
                />
              </div>
              <button type="submit" disabled={scanInput.length < 4}>
                <CheckCircle size={16} />
                Mark Delivered
              </button>
            </form>

            {recentScans.length > 0 && (
              <div className="recent-scans">
                <h4>Recent Scans ({recentScans.length})</h4>
                <div className="scans-list">
                  {recentScans.map((scan, idx) => (
                    <div key={idx} className={`scan-item ${scan.success ? 'success' : 'error'}`}>
                      <div className="scan-info">
                        {scan.success ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
                        <span className="scan-tracking">{scan.tracking}</span>
                        <span className="scan-arrow">→</span>
                        <span className="scan-customer">{scan.customer}</span>
                      </div>
                      <span className="scan-time">{scan.time}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="delivery-filters">
        <div className="search-input">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search by name, tracking, or phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <select 
          value={selectedParish} 
          onChange={(e) => setSelectedParish(e.target.value)}
          className="parish-filter"
        >
          <option value="all">All Parishes</option>
          {parishes.map(parish => (
            <option key={parish} value={parish}>{parish}</option>
          ))}
        </select>
      </div>

      {/* Kanban Board */}
      <div className="kanban-board">
        {COLUMNS.map(column => (
          <DeliveryColumn
            key={column.id}
            column={column}
            groupedPackages={groupedByStatus[column.id] || {}}
            onStartDelivery={handleStartDelivery}
            onCompleteDelivery={handleCompleteDelivery}
          />
        ))}
      </div>

      {/* Complete Delivery Dialog */}
      {showCompleteDialog && (
        <div className="dialog-overlay" onClick={() => setShowCompleteDialog(false)}>
          <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <div className="dialog-title">
                <CheckCircle size={20} />
                <h2>Complete Delivery</h2>
                {selectedPackages.length > 1 && (
                  <span className="pkg-badge">{selectedPackages.length} packages</span>
                )}
              </div>
              <button className="close-btn" onClick={() => setShowCompleteDialog(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="dialog-body">
              {/* Customer Info */}
              <div className="customer-section">
                <User size={16} />
                <span className="customer-name">{selectedCustomer?.name}</span>
              </div>

              {/* Packages */}
              {selectedPackages.length > 1 && (
                <div className="packages-list">
                  {selectedPackages.map(pkg => (
                    <div key={pkg.id} className="package-row">
                      <span className="tracking">{pkg.tracking}</span>
                      <span className={`amount ${pkg.invoiceStatus !== 'paid' ? 'cod' : ''}`}>
                        {formatJMD(pkg.invoiceTotal)}
                        {pkg.invoiceStatus !== 'paid' && ' (COD)'}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* COD Payment */}
              {codTotal > 0 ? (
                <div className="cod-section">
                  <div className="cod-header">
                    <span>Total to Collect</span>
                    <span className="cod-amount">{formatJMD(codTotal)}</span>
                  </div>
                  <select 
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="payment-select"
                  >
                    <option value="cash">Cash</option>
                    <option value="card">Card</option>
                    <option value="bank_transfer">Bank Transfer</option>
                  </select>
                </div>
              ) : (
                <div className="prepaid-badge">
                  <CheckCircle size={16} />
                  All Prepaid
                </div>
              )}

              {/* Signature */}
              <div className="signature-section">
                <div className="signature-label">
                  <Pen size={14} />
                  <span>Customer Signature</span>
                  <span className="required">*Required</span>
                </div>
                <SignaturePad onSignatureChange={setHasSignature} />
              </div>

              {/* Notes */}
              <div className="notes-section">
                <label>Delivery Notes (Optional)</label>
                <textarea
                  placeholder="Any notes about the delivery..."
                  value={deliveryNotes}
                  onChange={(e) => setDeliveryNotes(e.target.value)}
                  rows={2}
                />
              </div>
            </div>

            <div className="dialog-footer">
              <button className="btn-cancel" onClick={() => setShowCompleteDialog(false)}>
                Cancel
              </button>
              <button 
                className="btn-confirm"
                onClick={handleConfirmDelivery}
                disabled={!hasSignature}
              >
                <CheckCircle size={16} />
                {codTotal > 0 ? 'Collect & Deliver' : 'Mark Delivered'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
